import { createContext, useEffect, useMemo, useState } from 'react';
import { Store, User, Franchise, System } from '../../app/services/api/generated';
import { Role } from '../../enums/Role';
import { useUser, useClerk } from '@clerk/clerk-react';

export interface UserType extends User {
  username?: string;
  roles?: string[];
}

interface UserContextType {
  user: UserType | null | undefined;

  selectedStore?: Store | null;
  selectedFranchise?: Franchise | null;
  selectedSystem?: System | null;

  stores: Store[],
  franchises: Franchise[],
  systems: System[],

  setUserContext: (user: User) => void;

  forceLogout: () => void;

  hasPermissions: () => boolean;
  hasPermissionTo: (action: Role[]) => boolean;
  setUserPermissions: (permissions: string[]) => void;
  isAdmin: () => boolean;
  isOrderlyAdmin: () => boolean;

  setSystems: (systems: System[]) => void;
  setFranchises: (stores: Franchise[]) => void;
  setStores: (stores: Store[]) => void;

  setSelectedStore: (store: Store | null) => void;
  setSelectedFranchise: (franchise: Franchise | null) => void;
  setSelectedSystem: (system: System | null) => void;
}

const initialContextState: UserContextType = {
  user: null,

  selectedStore: null,
  selectedFranchise: null,
  selectedSystem: null,

  stores: [],
  franchises: [],
  systems: [],

  setUserContext: (user: User) => { },

  forceLogout: () => { },

  hasPermissions: () => false,
  hasPermissionTo: (action: Role[]) => false,
  setUserPermissions: (permissions: string[]) => { },
  isAdmin: () => false,
  isOrderlyAdmin: () => false,

  setSystems: (systems: System[]) => { },
  setFranchises: (stores: Franchise[]) => { },
  setStores: (stores: Store[]) => { },

  setSelectedStore: (store: Store | null) => { },
  setSelectedFranchise: (franchise: Franchise | null) => { },
  setSelectedSystem: (system: System | null) => { },
};

export const UserContext = createContext<UserContextType>(initialContextState);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const UserContextProvider = ({ children }: any) => {
  const [user, setUser] = useState<UserType | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? (JSON.parse(storedUser)) : null;
  });

  const [selectedStore, setSelectedStore] = useState<Store | null>(() => {
    const storedStore = localStorage.getItem('selectedStore');
    return storedStore ? JSON.parse(storedStore) : null;
  });

  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(() => {
    const storedFranchise = localStorage.getItem('selectedFranchise');
    return storedFranchise ? JSON.parse(storedFranchise) : null;
  });

  const [selectedSystem, setSelectedSystem] = useState<System | null>(() => {
    const storedSystem = localStorage.getItem('selectedSystem');
    return storedSystem ? JSON.parse(storedSystem) : null;
  });

  const [stores, setStores] = useState<Store[]>(() => {
    const storedStores = localStorage.getItem('stores');
    return storedStores ? JSON.parse(storedStores) : [];
  });

  const [franchises, setFranchises] = useState<Franchise[]>(() => {
    const storedFranchises = localStorage.getItem('franchises');
    return storedFranchises ? JSON.parse(storedFranchises) : [];
  });

  const [systems, setSystems] = useState<System[]>(() => {
    const storedSystems = localStorage.getItem('systems');
    return storedSystems ? JSON.parse(storedSystems) : [];
  });

  const [userPermissions, setUserPermissions] = useState<string[]>(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    return storedPermissions ? JSON.parse(storedPermissions) : [];
  });

  const { isSignedIn, isLoaded } = useUser();
  const { signOut } = useClerk();

  useEffect(() => {
    if (!isSignedIn && isLoaded) {
      setUser(null);
      setSelectedStore(null);
      setSelectedFranchise(null);
      setSelectedSystem(null);
      setStores([]);
      setFranchises([]);
      setSystems([]);
      setUserPermissions([]);

      const language = localStorage.getItem('language');
      localStorage.clear();
      if (language) {
        localStorage.setItem('language', language);
        localStorage.setItem('i18nextLng', language);
      }

      if (window.location.pathname !== '/' &&
        window.location.pathname.indexOf('login') === -1) {
        window.location.assign('/');
      }
    }
  }, [isSignedIn]);

  useEffect(() => {
    localStorage.setItem('selectedStore', JSON.stringify(selectedStore));
  }, [selectedStore]);

  useEffect(() => {
    localStorage.setItem('selectedFranchise', JSON.stringify(selectedFranchise));
  }, [selectedFranchise]);

  useEffect(() => {
    localStorage.setItem('selectedSystem', JSON.stringify(selectedSystem));
  }, [selectedSystem]);

  useEffect(() => {
    localStorage.setItem('stores', JSON.stringify(stores));
  }, [stores]);

  useEffect(() => {
    localStorage.setItem('franchises', JSON.stringify(franchises));
  }, [franchises]);

  useEffect(() => {
    localStorage.setItem('systems', JSON.stringify(systems));
  }, [systems]);

  useEffect(() => {
    localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
  }, [userPermissions]);

  const contextValue = useMemo(() => {

    const hasPermissions = () => {
      if ((user?.roles?.length || 0) === 0) return false;

      return userPermissions?.length > 0;
    };

    const setUserContext = (userData: User | null) => {
      if (userData == null) return;

      const user: UserType = {
        ...userData,
        username: userData?.emailAddress ?? '',
        roles: userData?.permissions ?? [],
        stores: userData?.stores ?? [],
      };

      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
    };

    const hasPermissionTo = (actions: Role[]): boolean => {
      if (!user?.roles) return false;

      if (actions.some(a => a === 'User') && (user?.roles.length > 0)) {
        return true;
      }

      return actions.some(a => userPermissions?.map(r => r?.toLowerCase() ?? '').includes(a?.toLowerCase() ?? 'MISSING'));
    };

    const isAdmin = () => {
      if (!user?.roles) return false;
      return userPermissions.includes('admin');
    };

    const isOrderlyAdmin = () => {
      return user?.userLevel == 'Admin';
    };

    const setUserStores = (stores: Store[]) => {
      if (user == null) return;
      setUser({ ...user, stores });
      localStorage.setItem('user', JSON.stringify({ ...user, stores }));
    };

    const forceLogout = () => {
      signOut();
    };

    return {
      user,

      selectedStore,
      selectedFranchise,
      selectedSystem,

      stores,
      franchises,
      systems,

      setUserContext,

      forceLogout,

      hasPermissions,
      hasPermissionTo,
      setUserPermissions,
      isAdmin,
      isOrderlyAdmin,

      setSystems,
      setFranchises,
      setStores,
      setUserStores,

      setSelectedStore,
      setSelectedFranchise,
      setSelectedSystem,
    };
  }, [user, selectedStore, selectedFranchise, selectedSystem, stores, franchises, systems]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};